import { createSelector } from 'reselect';

import {
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
} from '@websky/graphql';
import { GdsBaggageAndVipServiceType, GdsServicesLimitInfo } from '../../../types';
import { CheckinState } from './reducers';
import { getGdsServicesCalculatedCountInfoSelector } from '@shared/lib/gdsService/getGdsServicesCalculatedCountInfoSelector';

const getCheckinOrder = (state: CheckinState) => state.order;

const getAllServices = createSelector(getCheckinOrder, order => order.additionalServices?.gdsServices?.services ?? []);

const getSegmentIds = createSelector(getCheckinOrder, order => order.segments?.map(segment => segment.id) ?? []);

const getTravellerIds = createSelector(
	getCheckinOrder,
	order => order.travellers?.map(traveller => traveller.id) ?? []
);

const getServicesRestrictionsByType = createSelector(
	getCheckinOrder,
	order => order.additionalServices?.gdsServices?.servicesRestrictionsByType ?? []
);

const getSelectedServices = (state: CheckinState) => state.selectedServices;

export const getGdsSelectedServicesCountInfo = createSelector(
	getAllServices,
	getSelectedServices,
	(gdsServices, selectedServices) => {
		const gdsServiceMap = new Map(gdsServices.map(service => [service.id, service]));

		const result: GdsServicesLimitInfo = {};

		for (const passengerId in selectedServices) {
			const passengerServices = selectedServices[passengerId];
			for (const serviceId in passengerServices) {
				const services = passengerServices[serviceId];
				for (const segmentId in services) {
					const serviceInfo = gdsServiceMap.get(serviceId);

					if (!serviceInfo) {
						console.warn(`Service ${serviceId} not found for ID: ${serviceId}`);
						continue;
					}

					const rfisc = serviceInfo.rfisc;
					const type = serviceInfo.type;
					const selectedCount = services[segmentId];

					let subType: GdsBaggageAndVipServiceType;

					if ((serviceInfo as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment).baggageType) {
						subType = (serviceInfo as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)
							.baggageType;
					}

					if ((serviceInfo as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType) {
						subType = (serviceInfo as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType;
					}

					// Initialize passenger entry if it doesn't exist
					if (!result[passengerId]) {
						result[passengerId] = {
							segments: {},
							total: { rfisc: {}, type: {}, subType: {} }
						};
					}

					if (!result[passengerId].segments[segmentId]) {
						result[passengerId].segments[segmentId] = {
							rfisc: {},
							type: {},
							subType: {}
						};
					}

					// Update total rfisc count
					result[passengerId].total.rfisc[rfisc] =
						(result[passengerId].total.rfisc[rfisc] || 0) + selectedCount;

					// Update total type count
					result[passengerId].total.type[type] = (result[passengerId].total.type[type] || 0) + selectedCount;

					// Update total subType count if subType exists
					if (subType) {
						result[passengerId].total.subType[subType] =
							(result[passengerId].total.subType[subType] || 0) + selectedCount;
					}

					// Update segment rfisc count
					result[passengerId].segments[segmentId].rfisc[rfisc] =
						(result[passengerId].segments[segmentId].rfisc[rfisc] || 0) + selectedCount;

					// Update segment type count
					result[passengerId].segments[segmentId].type[type] =
						(result[passengerId].segments[segmentId].type[type] || 0) + selectedCount;

					// Update segment subType count if subType exists
					if (subType) {
						result[passengerId].segments[segmentId].subType[subType] =
							(result[passengerId].segments[segmentId].subType[subType] || 0) + selectedCount;
					}
				}
			}
		}

		return result;
	}
);

export const getGdsServicesCalculatedCountInfo = createSelector(
	getTravellerIds,
	getSegmentIds,
	getServicesRestrictionsByType,
	getGdsSelectedServicesCountInfo,
	getGdsServicesCalculatedCountInfoSelector
);
